import Button from '@/components/NextUI/Button';
import Countdown from '@/components/Common/Countdown';
import Image from 'next/image';
import Link from 'next/link';
import React from 'react';
import useAuth from '@/hooks/useAuth';
import { useAppDispatch } from '@/hooks/redux';
import { showSide } from '@/store/slices/sideSlice';
import ProSide from '@/components/Common/Sides/partials/ProSide';
import usePaddlePricing from '../../hooks/usePaddlePricing';
import { useTranslations } from 'next-intl';
import {
  COMMON_EVENTS,
  logHubxEvent,
} from '@/app/providers/analytics';
import useCustomSearchParams from '@/hooks/useCustomSearchParams';
import {
  setFreeTypeForUserAccessLevel,
  setTypeForUserAccessLevel,
} from '@/store/slices/userAccessSlice';
import { useCheckoutIntentPrefetch } from '@/features/subscription/hooks/useCheckoutIntentPrefetch';
import { resolvePaywallPriceId } from '@/features/subscription/utils/pricing/resolvePaywallPrice';

const ToolLocked = ({
  data = {},
}: {
  data?: {
    variant?: 'tiered' | 'pro';
    lockDate?: string;
    featureName?: string;
    isCustom?: boolean;
    forceGetProCta?: boolean;
    customTitle?: string;
    customDescription?: string;
    image?: string;
    premiumDownloadEvent?: boolean;
    freeEvent?: boolean;
  };
}) => {
  const t = useTranslations('sides.tool_locked');
  const { hadFreeTrial, yearlyPrices, monthlyPrices, weeklyPrices } =
    usePaddlePricing();
  const dispatch = useAppDispatch();
  const { user } = useAuth();
  const { searchParams } = useCustomSearchParams();
  const toolSearchParam = searchParams.get('tool') as string;
  const shouldUseGetProCta = data?.forceGetProCta || hadFreeTrial;
  const { intentHandlers } = useCheckoutIntentPrefetch({
    getPriceParams: () => {
      const priceId = resolvePaywallPriceId({
        monthlyPrices,
        weeklyPrices,
        yearlyPrices,
        user,
      });
      if (!priceId) {
        return undefined;
      }

      const formattedTool = toolSearchParam
        ? toolSearchParam.replace(/-/g, '_')
        : '';

      return {
        priceId,
        toolType: toolSearchParam || undefined,
        paywallType: data.premiumDownloadEvent
          ? `premium_download_${formattedTool}`
          : undefined,
      };
    },
  });

  const showSideFunction = () => {
    if (user === null) {
      dispatch(
        showSide({ sideKey: 'register', sideData: { openPaywall: true } })
      );
    } else {
      dispatch(
        showSide({
          sideKey: 'paymentScreen',
          sideData: { variant: shouldUseGetProCta ? 'pro' : 'free' },
        })
      );
    }
    if (data.premiumDownloadEvent) {
      const formattedTool = toolSearchParam
        ? toolSearchParam.replace(/-/g, '_')
        : '';
      dispatch(setTypeForUserAccessLevel(`premium_download_${formattedTool}`));
      const download = localStorage.getItem('featureUsage_pdf-edit');

      const primaryPrice =
        yearlyPrices[0] ?? monthlyPrices[0] ?? weeklyPrices[0];

      logHubxEvent({
        eventName: COMMON_EVENTS.PURCHASE_BUTTON_CLICK,
        event_params: {
          paywallType: `premium_download_${formattedTool}`,
          currency: primaryPrice?.unit_price?.currency_code,
          amount: (primaryPrice?.unit_price?.amount ?? 0) / 100,
          product_id: primaryPrice?.product_id!,
        },
      });

      if (download && data.freeEvent !== false) {
        const downloadToolLocked = JSON.parse(download);
        if (downloadToolLocked.isLocked) {
          dispatch(setFreeTypeForUserAccessLevel(`premium_download_free`));
          setTimeout(() => {
            logHubxEvent({
              eventName: COMMON_EVENTS.PURCHASE_BUTTON_CLICK,
              event_params: {
                paywallType: 'premium_download_free',
                currency: primaryPrice?.unit_price?.currency_code,
                amount: (primaryPrice?.unit_price?.amount ?? 0) / 100,
                product_id: primaryPrice?.product_id!,
              },
            });
          }, 1000);
        }
      }
    }
  };

  return (
    <div className="w-full flex items-center lg:items-center justify-center bg-white min-h-dvh">
      <div className="w-full flex flex-col lg:flex-row lg:items-center h-full items-center justify-center lg:gap-[112px] p-4">
        <div className="flex flex-col items-center w-full">
          {!data?.isCustom && (
            <h2 className="text-center text-dark text-[32px] md:text-[40px] font-semibold">
              {data?.variant === 'pro'
                ? data?.featureName
                  ? t('pro_feature.named', { featureName: data.featureName })
                  : t('pro_feature.default')
                : t('free_limit')}
              <br />
              {t('cta')}
            </h2>
          )}

          <Image
            src={data?.image || '/images/try-7days-ok/banner.svg'}
            width={298}
            height={260}
            alt="try 7 days ok"
            className="mt-[32px]"
          />

          {!data?.isCustom && (
            <p className="text-gray-medium text-[17px] md:text-lg mt-[32px] text-center">
              {t('unlock_message')}
            </p>
          )}

          <div className="flex justify-center mt-2">
            {data?.variant === 'tiered' && data?.lockDate && (
              <Countdown dateTime={data.lockDate} />
            )}
          </div>

          {!data?.isCustom && (
            <p className="text-gray-medium text-[17px] md:text-lg mt-2 text-center">
              {t('cancel_anytime')}
            </p>
          )}

          {data?.isCustom && data?.customTitle && (
            <h2 className="text-center text-dark text-[32px] font-semibold mt-3">
              {data.customTitle}
            </h2>
          )}

          {data?.isCustom && data?.customDescription && (
            <p className="text-gray-medium text-[16px] -mt-1 text-center">
              {data.customDescription}
            </p>
          )}

          <Button
            onClick={showSideFunction}
            {...intentHandlers}
            className="w-[160px] mt-[12px]"
          >
            {shouldUseGetProCta ? t('buttons.get_pro') : t('buttons.try_free')}
          </Button>

          {user === null && (
            <p className="text-gray-medium mt-2 text-center">
              {t('login_prompt')}{' '}
              <Link
                onClick={() => dispatch(showSide({ sideKey: 'login' }))}
                href={''}
              >
                <span className="text-primary">{t('login_link')}</span>
              </Link>
            </p>
          )}
        </div>
      </div>
      {/* <ProSide /> */}
    </div>
  );
};

export default ToolLocked;
